import { t as uiText } from '../i18n';
﻿// src/components/BoardList.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { isNcgSeries } from '../features/plans/series';
import { useHashtagContext } from '../context/HashtagContext';
import { useSearchContext } from '../context/SearchContext';
import { collection, query, orderBy, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../context/AuthContext';
import { searchAllBoards, searchBoardsByHashtag } from '../features/search/searchUtils';
import { PreviewData } from '../features/collaboration/searchDataUtils';
import { getBoardHashtags } from '../features/hashtag/hashtagUtils';
import { queryKeys } from '../lib/queryKeys';
import { searchAllBoards as apiSearchAllBoards, searchBoardsByHashtag as apiSearchBoardsByHashtag, BoardData as ApiBoardData } from '../api/boards';
import { useBoardMutations } from '../hooks/useBoardMutations';
import { useFolderContext } from '../context/FolderContext';
import NotePreviewCard from './NotePreviewCard';
import { CrawlerPreviewModal } from './CrawlerPreviewModal';
import folderIcon from '../assets/image/icon/folder.png';
import './BoardList.css';
import { workspacePagePath } from '../utils/workspaceNavigation';



interface BoardData {
  id: string;
  title?: string;
  content?: string;
  firstImageUrl?: string | null;
  lastModified?: any;
  lastModifiedBy?: string;
  version?: number;
  slug?: string;
  archived?: boolean;
  pinned?: boolean;
  // 新しいDB構造フィールド
  previewData?: PreviewData;
  searchData?: {
    hashtags?: string[];
    originalContent?: string;
    originalContentTextCount?: number;
  };
  hashtags?: string[]; // 互換性のために残す（searchData.hashtagsを優先）
  createdAt?: any;
  publicPage?: { status?: string; published?: boolean; webPageEnabled?: boolean };
  publicPageStatus?: 'nonPublic' | 'standby' | 'published';
  publicPageStatusCategory?: 1 | 2 | 3;
  /** 知識軸: AIの回答に使うか */
  publicRagEnabled?: boolean;
  /** ページ軸: Webページとして公開する意図があるか */
  publicWebIntended?: boolean;
  // フォルダー情報
  folderId?: string | null; // 所属フォルダーID（nullの場合はルートフォルダー）
  ancestorFolderIds?: string[]; // 祖先フォルダーIDの配列
  // クローラー情報
  isCrawled?: boolean; // クローラーで取得されたページかどうか
}

interface BoardListProps {
  isSelectionMode?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  selectedBoardIds?: string[];
  refreshTrigger?: number;
  onBoardsChange?: (boards: BoardData[]) => void;
  sortOrder?: 'asc' | 'desc';
  sortBy?: 'lastModified' | 'createdAt' | 'title' | 'contentSize';
  /** ワークスペースの「ページをWeb公開する」。null は読み込み中 */
  htmlPagesEnabled?: boolean | null;
}



const BoardList: React.FC<BoardListProps> = ({
  isSelectionMode = false,
  onSelectionChange,
  selectedBoardIds = [],
  refreshTrigger = 0,
  onBoardsChange,
  sortOrder = 'desc',
  sortBy = 'lastModified',
  htmlPagesEnabled = null,
}) => {
  const navigate = useNavigate();
  const { workspaceId: routeWorkspaceId } = useParams();
  const { workspaces } = useWorkspaceContext();
  const { selectedHashtag, setSelectedHashtag, hashtagRefreshTrigger } = useHashtagContext();
  const { searchQuery, isSearching, setSearchQuery, setIsSearching } = useSearchContext();
  const { user } = useAuthContext();
  const { togglePin } = useBoardMutations();
  const { selectedFolderId, setSelectedFolderId, folders } = useFolderContext();
  const routeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === routeWorkspaceId) || null,
    [workspaces, routeWorkspaceId]
  );
  const isWorkspacePublicVisualEnabled = isNcgSeries(routeWorkspace?.seriesId);

  // クローラープレビューモーダルの状態
  const [showCrawlerModal, setShowCrawlerModal] = useState(false);
  const [selectedCrawlerBoard, setSelectedCrawlerBoard] = useState<BoardData | null>(null);

  // レスポンシブ対応用の状態
  const [isMobile, setIsMobile] = useState(false);

  // レスポンシブ対応のウィンドウサイズ監視
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // 初期チェック
    checkIsMobile();
    
    // リサイズイベントリスナーを追加
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // データ取得関数
  const fetchBoards = async (): Promise<{ mainBoards: BoardData[]; childFolderBoards: BoardData[] }> => {
    if (!routeWorkspaceId || !user) {
      return { mainBoards: [], childFolderBoards: [] };
    }

    const normalizeHashtagDocId = (tagName: string) =>
      tagName
        .replace(/[\/\[\]]/g, '_')
        .replace(/\s+/g, '_')
        .substring(0, 1500);

    let boardsData: ApiBoardData[] = [];

    try {
      if (selectedHashtag) {
        boardsData = await apiSearchBoardsByHashtag(routeWorkspaceId, selectedHashtag, searchQuery);
      } else {
        boardsData = await apiSearchAllBoards(routeWorkspaceId, searchQuery);
      }

    } catch (error) {
      console.error('❌ BoardList: ボード検索でエラー', error);
      throw error;
    }

    const boardsWithHashtags: BoardData[] = boardsData.map((board) => ({
      ...board,
      searchData: {
        ...board.searchData,
        hashtags: board.searchData?.hashtags || [],
      },
      hashtags: board.searchData?.hashtags || [],
    }));

    // ✅ hashtags コレクションを正として、一覧を強制的に整合させる
    try {
      if (selectedHashtag) {
        const hashtagDocRef = doc(
          db,
          `workspaces/${routeWorkspaceId}/hashtags/${normalizeHashtagDocId(selectedHashtag)}`
        );
        const hashtagSnap = await getDoc(hashtagDocRef);
        const hashtagBoards = (hashtagSnap.exists() ? ((hashtagSnap.data() as any)?.boards || []) : []) as string[];

        if (hashtagSnap.exists()) {
          const allowedSet = new Set(hashtagBoards);
          const filtered = boardsWithHashtags.filter((b) => allowedSet.has(b.id));
          boardsWithHashtags.length = 0;
          boardsWithHashtags.push(...filtered);
        }
      }
    } catch {}

    // フォルダーIDでフィルタリング
    const filteredBoards = selectedFolderId 
      ? boardsWithHashtags.filter((board: BoardData) => board.folderId === selectedFolderId)
      : boardsWithHashtags;

    // 子フォルダーのボードをフィルタリング（ancestorFolderIdsにselectedFolderIdが含まれるが、folderIdが異なるもの）
    const childFolderBoards = selectedFolderId 
      ? boardsWithHashtags.filter((board: BoardData) => 
          board.ancestorFolderIds?.includes(selectedFolderId) && 
          board.folderId !== selectedFolderId
        )
      : [];

    // ピンされたボードを最初に、その後は sortBy に応じてソート
    const getSortTime = (board: BoardData): number => {
      const lm = board.lastModified;
      if (lm == null || lm === 0) return Number.MAX_SAFE_INTEGER;
      if (typeof lm === 'object' && 'toDate' in lm && typeof (lm as any).toDate === 'function')
        return (lm as any).toDate().getTime();
      const t = new Date(lm as any).getTime();
      return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
    };
    const getCreatedAtTime = (board: BoardData): number => {
      const ca = board.createdAt;
      if (ca == null) return 0;
      if (typeof ca === 'object' && 'toDate' in ca && typeof (ca as any).toDate === 'function')
        return (ca as any).toDate().getTime();
      const t = new Date(ca as any).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const getTitleSortKey = (board: BoardData): string =>
      (board.title ?? '').trim().toLowerCase();
    const getContentSize = (board: BoardData): number =>
      (board.searchData?.originalContentTextCount ?? (board as any).previewData?.structuredData?.wordCount ?? 0);

    const compare = (a: BoardData, b: BoardData): number => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      let diff = 0;
      switch (sortBy) {
        case 'lastModified':
          diff = getSortTime(b) - getSortTime(a);
          break;
        case 'createdAt':
          diff = getCreatedAtTime(b) - getCreatedAtTime(a);
          break;
        case 'title':
          diff = getTitleSortKey(a).localeCompare(getTitleSortKey(b), 'ja');
          break;
        case 'contentSize':
          diff = getContentSize(b) - getContentSize(a);
          break;
        default:
          diff = getSortTime(b) - getSortTime(a);
      }
      return sortOrder === 'asc' ? (sortBy === 'title' ? diff : -diff) : (sortBy === 'title' ? -diff : diff);
    };

    filteredBoards.sort(compare);

    // 子フォルダーボードも同様にソート
    childFolderBoards.sort(compare);

    if (import.meta.env.MODE === 'development') {
      filteredBoards.forEach((board, index) => {});
      childFolderBoards.forEach((board, index) => {});
    }

    return { mainBoards: filteredBoards as BoardData[], childFolderBoards: childFolderBoards as BoardData[] };
  };

  // デバッグ: enabled条件とqueryKeyの詳細ログ
  const enabledCondition = !!routeWorkspaceId && !!user;
  const baseQueryKey = queryKeys.boards.list(routeWorkspaceId ?? '', selectedHashtag || undefined, searchQuery || undefined, selectedFolderId);
  // refreshTrigger・sortOrder・sortByを含めてキャッシュを無効化
  const queryKey = [...baseQueryKey, hashtagRefreshTrigger, refreshTrigger, sortOrder, sortBy];

  const {
    data: boardData = { mainBoards: [], childFolderBoards: [] },
    isLoading: loading,
    error,
    dataUpdatedAt,
    isFetching,
    isStale,
  } = useQuery({
    queryKey: [...queryKey, 'full'],
    queryFn: () => fetchBoards(),
    enabled: enabledCondition,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });

  const { mainBoards, childFolderBoards } = boardData;

  // onBoardsChangeの参照をuseRefで管理して無限ループを防止
  const onBoardsChangeRef = useRef(onBoardsChange);

  useEffect(() => {
    onBoardsChangeRef.current = onBoardsChange;
  }, [onBoardsChange]);

  // boards の内容が実際に変わった時だけ通知する
  const prevBoardsRef = useRef<BoardData[]>([]);

  useEffect(() => {
    // mainBoards の ID リストが変わった時だけ通知
    const allBoards = [...mainBoards, ...childFolderBoards];
    const currentIds = allBoards.map(b => b.id).sort().join(',');
    const prevIds = prevBoardsRef.current.map(b => b.id).sort().join(',');
    
    if (currentIds !== prevIds) {
      prevBoardsRef.current = allBoards;
      if (onBoardsChangeRef.current) {
        onBoardsChangeRef.current(allBoards);
      }
    }
  }, [mainBoards, childFolderBoards]);

  // エラーログの追加
  useEffect(() => {
    if (error) {
      console.error('❌ BoardList: useQueryエラー', { 
        error, 
        errorObject: error,
        routeWorkspaceId, 
        selectedHashtag, 
        searchQuery,
        user: !!user,
        userObject: user,
        enabledCondition,
        baseQueryKey,
        queryKey,
        refreshTrigger,
        timestamp: new Date().toISOString()
      });
    }
  }, [error, routeWorkspaceId, selectedHashtag, searchQuery, user, enabledCondition, baseQueryKey, queryKey, refreshTrigger]);

  // デバッグログ（開発環境のみ）
  if (import.meta.env.MODE === 'development') {
    // 各ボードのタイトル状況を詳細確認
    mainBoards.forEach((board, index) => {});
    childFolderBoards.forEach((board, index) => {});
  }

  const handleCreateBoard = () => {
    if (routeWorkspaceId) {
      // 現在のURLパラメータを取得して embed=true を引き継ぐ
      const currentParams = new URLSearchParams(window.location.search);
      const embedParam = currentParams.get('embed');
      
      let url = selectedHashtag 
        ? `/${routeWorkspaceId}/new?hashtag=${encodeURIComponent(selectedHashtag)}`
        : `/${routeWorkspaceId}/new`;
      
      // embed=true がある場合は引き継ぐ
      if (embedParam === 'true') {
        url += url.includes('?') ? '&embed=true' : '?embed=true';
      }

      // 同じURLへの遷移の場合は強制的にページを更新（念のため）
      const isSameUrl = window.location.pathname === url.split('?')[0];
      if (isSameUrl) {
        // 同じURLの場合は一度リロードして確実に初期化
        window.location.href = url;
        return;
      }

      navigate(url, { replace: false }); // 新規作成時は履歴に残す
    } else {
      console.warn('⚠️ BoardList: ワークスペースが選択されていません');
    }
  };

  const handleBoardClick = (slug: string) => {
    if (routeWorkspaceId) {
      // 現在のURLパラメータを取得して embed=true を引き継ぐ
      const currentParams = new URLSearchParams(window.location.search);
      const embedParam = currentParams.get('embed');
      
      let url = workspacePagePath(routeWorkspaceId, slug);
      
      // embed=true がある場合は引き継ぐ
      if (embedParam === 'true') {
        url += '?embed=true';
      }
      
      navigate(url);
    } else {}
  };
  
  // クローラーページクリック時の処理
  const handleCrawlerClick = (board: BoardData) => {
    setSelectedCrawlerBoard(board);
    setShowCrawlerModal(true);
  };

  const handleBoardSelect = (boardId: string) => {
    if (!onSelectionChange) return;
    
    const newSelectedIds = selectedBoardIds.includes(boardId)
      ? selectedBoardIds.filter(id => id !== boardId)
      : [...selectedBoardIds, boardId];
    
    onSelectionChange(newSelectedIds);
  };

  const handlePinToggle = async (boardId: string, currentPinned: boolean) => {
    if (!routeWorkspaceId) return;
    
    try {
      await togglePin({
        workspaceId: routeWorkspaceId,
        boardId,
        pinned: !currentPinned
      });
    } catch (error) {
      console.error('ピン状態の更新エラー:', error);
    }
  };

  // ピンされたボードと通常のボードを分離（メインフォルダー）— フックより前に算出
  const pinnedMainBoards = mainBoards.filter((board: BoardData) => !board.archived && board.pinned);
  const normalMainBoards = mainBoards.filter((board: BoardData) => !board.archived && !board.pinned);

  // --- 仮想グリッド（フックは早期リターンより前に配置する必要がある） ---
  type GridItem = { type: 'new' } | { type: 'board'; board: BoardData };
  const gridItems: GridItem[] = useMemo(() => [
    { type: 'new' as const },
    ...normalMainBoards.map(board => ({ type: 'board' as const, board })),
  ], [normalMainBoards]);

  const gridContainerRef = useRef<HTMLDivElement>(null);
  const probeRowRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(1);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [containerReady, setContainerReady] = useState(false);

  useEffect(() => {
    const container = gridContainerRef.current;
    const probe = probeRowRef.current;
    if (!container || !probe) return;

    const ro = new ResizeObserver(() => {
      const cols = window.getComputedStyle(probe).gridTemplateColumns.split(' ').length;
      setScrollMargin(container.offsetTop);
      setColumnCount(Math.max(1, cols));
      setContainerReady(true);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [loading]);

  const GAP = isMobile ? 8 : 16;
  const ROW_HEIGHT = (isMobile ? 240 : 282) + GAP;
  const rowCount = Math.ceil(gridItems.length / columnCount);

  const estimateSize = useCallback(() => ROW_HEIGHT, [ROW_HEIGHT]);
  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize,
    overscan: 3,
    scrollMargin,
  });
  // --- 仮想グリッド ここまで ---

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div>{uiText("Loading now...")}</div>
      </div>
    );
  }

  // フォルダー名を取得するヘルパー関数
  const getFolderName = (folderId: string | null): string => {
    if (!folderId) {
      return uiText("Root folder");
    }
    
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.name || uiText("Untitled folder") : uiText("Unknown folder");
  };

  // 検索結果とフォルダー選択状態の表示
  const renderSearchInfo = () => {
    const hasSearchOrHashtag = isSearching || selectedHashtag;
    const hasFolderSelection = selectedFolderId !== null;
    
    if (!hasSearchOrHashtag && !hasFolderSelection) {
      return null;
    }

    let searchText: string | React.ReactElement = '';
    if (isSearching && selectedHashtag) {
      const totalCount = mainBoards.length + childFolderBoards.length;
      searchText = uiText("Results for \"{{searchQuery}}\" in #{{selectedHashtag}}: {{totalCount}} items", { selectedHashtag: selectedHashtag, searchQuery: searchQuery, totalCount: totalCount });
    } else if (isSearching) {
      const totalCount = mainBoards.length + childFolderBoards.length;
      searchText = uiText("Results for \"{{searchQuery}}\": {{totalCount}} items", { searchQuery: searchQuery, totalCount: totalCount });
    } else if (selectedHashtag) {
      const totalCount = mainBoards.length + childFolderBoards.length;
      searchText = uiText("Boards for #{{selectedHashtag}}: {{totalCount}} items", { selectedHashtag: selectedHashtag, totalCount: totalCount });
    } else if (hasFolderSelection) {
      const folderName = getFolderName(selectedFolderId);
      const mainFolderCount = mainBoards.length; // メインフォルダーのみの件数

      searchText = (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <img 
            src={folderIcon} 
            alt={uiText("Folder")} 
            style={{ 
              width: '16px', 
              height: '16px', 
              filter: 'grayscale(100%) brightness(0.6)',
              opacity: 0.7
            }} 
          />
          <span>{folderName} {uiText(" boards:")} {mainFolderCount}{uiText(" items")}</span>
        </span>
      );
    }

    return (
      <div style={{
        marginBottom: '20px',
        fontSize: '14px',
        color: 'var(--workspace-primary, #cfae91)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>{searchText}</span>
        {(selectedHashtag || hasFolderSelection) && (
          <button
            onClick={() => {
              setSelectedHashtag(null);
              setSearchQuery('');
              setIsSearching(false);
              setSelectedFolderId(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--workspace-primary, #cfae91)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textDecoration: 'underline'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.color = 'var(--workspace-primary-dark, #b89a7a)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.currentTarget.style.color = 'var(--workspace-primary, #cfae91)';
              }
            }}
          >
            {uiText("Back to all boards")}
          </button>
        )}
      </div>
    );
  };

  // 子フォルダーボードをfolderIdごとにグループ化
  const groupedChildBoards = childFolderBoards.reduce((groups: Record<string, BoardData[]>, board: BoardData) => {
    const folderId = board.folderId || 'unknown';
    if (!groups[folderId]) {
      groups[folderId] = [];
    }
    groups[folderId].push(board);
    return groups;
  }, {});

  // 各グループ内でピンされたボードと通常のボードを分離
  const groupedChildBoardsWithPins = Object.entries(groupedChildBoards).map(([folderId, boards]) => ({
    folderId,
    folderName: getFolderName(folderId),
    pinnedBoards: boards.filter((board: BoardData) => !board.archived && board.pinned),
    normalBoards: boards.filter((board: BoardData) => !board.archived && !board.pinned)
  }));

  // エラーハンドリング
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '16px', color: '#dc3545' }}>
          {uiText("Failed to load board")}
        </div>
      </div>
    );
  }

  return (
    <div>
      {renderSearchInfo()}
      
      {/* メインフォルダーのピンされたボードセクション */}
      {pinnedMainBoards.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div 
            data-testid="board-list-container"
            className={`board-list-container ${pinnedMainBoards.length > 1 ? 'multi-column' : ''}`}
          >
            {pinnedMainBoards.map((board: BoardData) => {
              return (
                <NotePreviewCard
                  key={board.id}
                  id={board.id}
                  title={board.title}
                  content={board.content}
                  firstImageUrl={board.firstImageUrl}
                  lastModified={board.lastModified}
                  slug={board.slug}
                  pinned={board.pinned}
                  archived={board.archived}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedBoardIds.includes(board.id)}
                  isMobile={isMobile}
                  onCardClick={handleBoardClick}
                  onSelectionChange={handleBoardSelect}
                  onPinToggle={handlePinToggle}
                  showPinButton={true}
                  hashtags={board.hashtags}
                  previewData={board.previewData}
                  publicPageStatus={board.publicPageStatus}
                  publicRagEnabled={board.publicRagEnabled}
                  publicWebIntended={board.publicWebIntended}
                  webPagesEnabled={htmlPagesEnabled !== false}
                  enablePublicVisuals={isWorkspacePublicVisualEnabled}
                  isCrawled={board.isCrawled}
                  onCrawlerClick={() => handleCrawlerClick(board)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* メインフォルダーの通常のボードセクション（仮想スクロール） */}
      <div ref={gridContainerRef} data-testid="board-list-container">
        {/* CSS auto-fill の列数を計測するプローブ行（非表示） */}
        <div
          ref={probeRowRef}
          className="board-list-container"
          style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }}
        />
        <div style={{
          height: containerReady ? virtualizer.getTotalSize() : 0,
          width: '100%',
          position: 'relative',
          visibility: containerReady ? 'visible' : 'hidden',
        }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const startIdx = virtualRow.index * columnCount;
            const rowItems = gridItems.slice(startIdx, startIdx + columnCount);

            return (
              <div
                key={virtualRow.key}
                className="board-list-container"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                }}
              >
                {rowItems.map((item, colIdx) =>
                  item.type === 'new' ? (
                    <NotePreviewCard
                      key="new-card"
                      showNewCardButton={true}
                      isMobile={isMobile}
                      onClick={handleCreateBoard}
                      id=""
                      title=""
                      content=""
                      lastModified={null}
                      slug=""
                      hashtags={[]}
                    />
                  ) : (
                    <NotePreviewCard
                      key={item.board.id}
                      id={item.board.id}
                      title={item.board.title}
                      content={item.board.content}
                      firstImageUrl={item.board.firstImageUrl}
                      lastModified={item.board.lastModified}
                      slug={item.board.slug}
                      pinned={item.board.pinned}
                      archived={item.board.archived}
                      isSelectionMode={isSelectionMode}
                      isSelected={selectedBoardIds.includes(item.board.id)}
                      isMobile={isMobile}
                      onCardClick={handleBoardClick}
                      onSelectionChange={handleBoardSelect}
                      onPinToggle={handlePinToggle}
                      showPinButton={true}
                      hashtags={item.board.hashtags}
                      previewData={item.board.previewData}
                      publicPageStatus={item.board.publicPageStatus}
                      publicRagEnabled={item.board.publicRagEnabled}
                      publicWebIntended={item.board.publicWebIntended}
                      webPagesEnabled={htmlPagesEnabled !== false}
                      enablePublicVisuals={isWorkspacePublicVisualEnabled}
                      isCrawled={item.board.isCrawled}
                      onCrawlerClick={() => handleCrawlerClick(item.board)}
                    />
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 子フォルダーのボードセクション */}
      {groupedChildBoardsWithPins.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: 'var(--workspace-primary, #cfae91)', 
            marginBottom: '16px',
            paddingLeft: '8px'
          }}>
            {uiText("Boards in subfolders")}
          </h3>
          
          {/* 各フォルダーグループを表示 */}
          {groupedChildBoardsWithPins.map((group) => (
            <div key={group.folderId} style={{ marginBottom: '24px' }}>
              {/* フォルダー名タイトル */}
              <div style={{ 
                fontSize: '14px', 
                color: 'var(--workspace-primary, #cfae91)',
                marginBottom: '12px',
                paddingLeft: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <img 
                  src={folderIcon} 
                  alt={uiText("Folder")} 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    filter: 'grayscale(100%) brightness(0.6)',
                    opacity: 0.7
                  }} 
                />
                <span>{group.folderName} {uiText(" boards:")} {group.pinnedBoards.length + group.normalBoards.length}{uiText(" items")}</span>
              </div>
              
              {/* ピンされたボード */}
              {group.pinnedBoards.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div 
                    data-testid="child-board-list-container"
                    className={`board-list-container ${group.pinnedBoards.length > 1 ? 'multi-column' : ''}`}
                  >
                    {group.pinnedBoards.map((board: BoardData) => {
                      return (
                        <NotePreviewCard
                          key={board.id}
                          id={board.id}
                          title={board.title}
                          content={board.content}
                          firstImageUrl={board.firstImageUrl}
                          lastModified={board.lastModified}
                          slug={board.slug}
                          pinned={board.pinned}
                          archived={board.archived}
                          isSelectionMode={isSelectionMode}
                          isSelected={selectedBoardIds.includes(board.id)}
                          isMobile={isMobile}
                          onCardClick={handleBoardClick}
                          onSelectionChange={handleBoardSelect}
                          onPinToggle={handlePinToggle}
                          showPinButton={true}
                          hashtags={board.hashtags}
                          previewData={board.previewData}
                          publicPageStatus={board.publicPageStatus}
                          publicRagEnabled={board.publicRagEnabled}
                          publicWebIntended={board.publicWebIntended}
                          webPagesEnabled={htmlPagesEnabled !== false}
                          enablePublicVisuals={isWorkspacePublicVisualEnabled}
                          isCrawled={board.isCrawled}
                          onCrawlerClick={() => handleCrawlerClick(board)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 通常のボード */}
              {group.normalBoards.length > 0 && (
                <div>
                  <div 
                    data-testid="child-board-list-container"
                    className={`board-list-container ${group.normalBoards.length > 1 ? 'multi-column' : ''}`}
                  >
                    {group.normalBoards.map((board: BoardData) => {
                      return (
                        <NotePreviewCard
                          key={board.id}
                          id={board.id}
                          title={board.title}
                          content={board.content}
                          firstImageUrl={board.firstImageUrl}
                          lastModified={board.lastModified}
                          slug={board.slug}
                          pinned={board.pinned}
                          archived={board.archived}
                          isSelectionMode={isSelectionMode}
                          isSelected={selectedBoardIds.includes(board.id)}
                          isMobile={isMobile}
                          onCardClick={handleBoardClick}
                          onSelectionChange={handleBoardSelect}
                          onPinToggle={handlePinToggle}
                          showPinButton={true}
                          hashtags={board.hashtags}
                          previewData={board.previewData}
                          publicPageStatus={board.publicPageStatus}
                          publicRagEnabled={board.publicRagEnabled}
                          publicWebIntended={board.publicWebIntended}
                          webPagesEnabled={htmlPagesEnabled !== false}
                          enablePublicVisuals={isWorkspacePublicVisualEnabled}
                          isCrawled={board.isCrawled}
                          onCrawlerClick={() => handleCrawlerClick(board)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* クローラープレビューモーダル */}
      <CrawlerPreviewModal
        isOpen={showCrawlerModal}
        onClose={() => {
          setShowCrawlerModal(false);
          setSelectedCrawlerBoard(null);
        }}
        boardData={selectedCrawlerBoard}
      />
    </div>
  );
};

export default BoardList;
